import { Component, ChangeDetectionStrategy, inject, AfterViewInit, input } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { extractRouteFromURL } from '@shared/helpers';

import {
  AboutPanelComponent,
  IconV3Component,
  NavigationItem,
  SidebarV3Component,
  SidePanelV3,
  SidepanelTypesV3,
} from '@gea/digital-ui-lib';
import { PACKAGE_VERSION } from '@shared/utils/tokens';
import { BRCVersionComponent } from './version/version.component';
import { NavigationEnd } from '@angular/router';
import { filter } from 'rxjs';
import { BRCBaseComponent } from '@brc/hrt-base-component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'gea-hrt-sidebar',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslatePipe, IconV3Component, SidebarV3Component],
  template: `
    <div class="side-bar">
      @if (navigationItems().length) {
        <geaui-sidebar-v3
          [devisionName]="'GENERAL.TITLE' | translate"
          [navigationItems]="navigationItems()"
          devisionIconPath="assets/images/GEA-Insight-partner-blue-red-care.svg"
          [bottomNavigation]="bottomNavigation">
          <a href="mailto:ber.bluredcare.doc@gea.com?subject=Regarding BLU-RED-Care:" class="contact-link">
            <geaui-icon-v3 file="letter" [fileSize]="16" class="icon-mail" />
            <span class="contact-text">{{ 'CONTACT_TEXT' | translate }}</span>
          </a>
        </geaui-sidebar-v3>
      }
    </div>
  `,
})
export class SidebarComponent extends BRCBaseComponent implements AfterViewInit {
  readonly AboutPanelComponent = AboutPanelComponent;
  readonly packageVersion = inject(PACKAGE_VERSION);

  readonly currentLanguage = input.required<string>();
  readonly navigationItems = input.required<NavigationItem[]>();

  ngAfterViewInit(): void {
    const updateSelected = (url: string) => {
      document
        .querySelectorAll('.sidebar ul li div.p-tree-node-selected')
        .forEach((el) => el.classList.remove('p-tree-node-selected'));
      const route = extractRouteFromURL(url) ?? '';
      const selector = `.sidebar ul li[aria-label="TAB_CONTAINER.TABS.${route.replace('-', '_').toUpperCase()}"] div`;
      document.querySelector(selector)?.classList.add('p-tree-node-selected');
    };

    updateSelected(this.router.url);

    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((nav) => updateSelected(nav.url));
  }

  private openDocument(type: 'Dataprivacy' | 'TandC' | 'Imprint'): void {
    const lang = this.currentLanguage() || 'en-US';
    window.open(`../../assets/documents/${type}${lang}.pdf`, '_blank');
  }

  openDataPrivacy(): void {
    this.openDocument('Dataprivacy');
  }

  openTermsAndConditions(): void {
    this.openDocument('TandC');
  }

  openImprint(): void {
    this.openDocument('Imprint');
  }

  openCookieSettings(): void {
    // @ts-expect-error OneTrust is expected to be available globally
    window.OneTrust?.ToggleInfoDisplay();
  }

  bottomNavigation: SidePanelV3[] = [
    {
      key: 'about',
      icon: 'c-info',
      label: 'NAV-BAR.ABOUT',
      type: SidepanelTypesV3.FOOTER,
      ref: AboutPanelComponent,
      inputs: {
        copyright: 'GEA Group Services GmbH 2024',
        footers: [
          {
            key: 'imprint',
            header: 'IMPRINT',
            content: [
              {
                key: 'UI-LIB.FOOTER.imprint',
                icon: 'pdf',
                label: 'UI-LIB.FOOTER.IMPRINT',
                action: this.openImprint.bind(this),
              },
            ],
          },
          {
            key: 'privacy',
            header: 'DATAPRIVACY',
            content: [
              {
                key: 'UI-LIB.FOOTER.privacy',
                icon: 'pdf',
                label: 'UI-LIB.FOOTER.DATAPRIVACY',
                action: this.openDataPrivacy.bind(this),
              },
            ],
          },
          {
            key: 'terms',
            header: 'TERMS-AND-CONDITIONS',
            content: [
              {
                key: 'UI-LIB.FOOTER.terms',
                icon: 'pdf',
                label: 'UI-LIB.FOOTER.TERMS-AND-CONDITIONS',
                action: this.openTermsAndConditions.bind(this),
              },
            ],
          },
          {
            key: 'cookies',
            header: 'COOKIE-SETTINGS',
            content: [
              {
                key: 'UI-LIB.FOOTER.cookies',
                icon: 'pdf',
                label: 'UI-LIB.FOOTER.COOKIES',
                action: this.openCookieSettings,
              },
            ],
          },
        ],
      },
    },
    {
      key: 'appVersions',
      icon: 'new-notification',
      label: `Version ${this.packageVersion}`,
      type: SidepanelTypesV3.FOOTER,
      ref: BRCVersionComponent,
      inputs: {
        copyright: 'GEA Group Services GmbH 2024',
        footers: [],
      },
    },
  ];
}
